import { icons } from '@/assets/icons';
import { howItWorks } from '@/assets/materials';
import type { CoreItem } from '@/components/bases/CoreLaunchPadCard';
import type { FAQItem, HowItWorkItem } from '@/global/types/common.type';

export const CORE_ITEMS: CoreItem[] = [
  {
    id: 1,
    icon: icons.ic_flash,
    title: 'Instant Deployment',
    description:
      'Say goodbye to long, ambiguous approval processes and unnecessary gatekeeping. Your core sale is ready to launch—giving you full control over your fundraising with a seamless, efficient process.',
  },
  {
    id: 2,
    icon: icons.ic_shapes,
    title: 'Simple & Intuitive UI',
    description:
      'You don’t need to be a technical expert to launch a core sale. Our user-friendly interface is designed for speed, ease, and efficiency. Every step of the process is streamlined, making it accessible for founders, investors, and community members alike.',
  },
  {
    id: 3,
    icon: icons.ic_tag,
    title: 'Self Funding',
    description:
      'Generate significant funding by selling core licenses, unlocking a lucrative revenue stream for further development.',
  },
  // {
  //   id: 4,
  //   icon: icons.ic_ocean,
  //   title: 'Full Transparency',
  //   description:
  //     'No hidden fees, no middlemen, no guesswork. LFG Core Launchpad operates on smart contracts for trustless, verifiable transactions with real-time tracking of core sales, uptime, and operator rewards—all secured and audited by Certik.',
  // },
  {
    id: 5,
    icon: icons.ic_box,
    title: 'Launch Your Core System',
    description: `Empower your project with independent infrastructure using our Core Technology. Seamlessly deploy, manage, and scale your network to enhance decentralization and performance.`,
  },
  {
    id: 6,
    icon: icons.ic_people,
    title: 'Grow & Reward Your Community',
    description:
      'Empower your community to operate cores, contribute to the network, and earn rewards—deepening their engagement while decentralizing control.',
  },
  // {
  //   id: 7,
  //   icon: icons.ic_link,
  //   title: 'Built for Multi-Chain Expansion',
  //   description:
  //     'Start your journey on Ethereum today, with future support planned for Base, Solana, and other networks LFG is committed to evolving with Web3, ensuring your project stays ahead as we expand to new networks.',
  // },
  // {
  //   id: 8,
  //   icon: icons.ic_cards,
  //   title: 'Credit Card Integration',
  //   description:
  //     'Accept payments via credit card, this feature bridges the gap between traditional finance and crypto, making core sales accessible to a broader audience beyond just Web3 natives.',
  // },
  {
    id: 9,
    icon: icons.ic_status_up,
    title: 'Year-over-Year Growth',
    description:
      'Core sales consistently outperform traditional token sales, making them a superior fundraising mechanism.',
  },
];

export const FAQ_ITEMS_SHORT: FAQItem[] = [
  {
    question: 'What is a Core Sale?',
    answer: `A fundraising method that lets projects sell cores to raise capital while decentralizing their network.`,
  },
  {
    question: 'How do Core owners earn rewards?',
    answer: 'Operators receive daily rewards based on uptime and network contribution in the project’s token.',
  },
  {
    question: 'Why are Core Sales better than traditional token sales?',
    answer: 'They distribute tokens gradually, reducing sell pressure and building a stronger community.',
  },
  {
    question: 'How much funding can a Core Sale generate?',
    answer: 'More than a typical token sale, thanks to a tiered pricing model that scales with demand.',
  },
  {
    question: 'How do I launch my own Core Sale?',
    answer:
      ' To launch your Core Sale, click "Launch App" at the top right corner of the site, and if you need any help along the way, join our LFG Discord community for support and guidance.',
  },
  {
    question: "Is the Platform's smart contract audited?",
    answer:
      'Yes, it is audited by Hashlock. The audit report link can be found on <a href="https://hashlock.com/audits/lfg" target="_blank">https://hashlock.com/audits/lfg</a>.',
  },
];

export const FAQ_ITEMS_FULL: FAQItem[] = [
  {
    question: 'What is a Core?',
    answer: `<p><span style="font-weight: 400;">A Core refers to a distributed workload, typically owned by an individual and run on a private computer to contribute to a wider network. Cores can perform various tasks, including data storage, transaction validation, and multimedia distribution.</span></p>
<p><span style="font-weight: 400;">Many Cores working on one network decentralize tasks, save computing power, and provide redundancy, reducing single points of failure. Core operators are rewarded with cryptocurrency, allowing them to gradually return on their investment.</span></p>`,
  },
  {
    question: 'What Are Core Sales?',
    answer: `<p><span style="font-weight: 400;">A Core Sale is an early fundraising method for web3 projects that also establishes a loyal community and network. Projects sell Cores to their community, raising capital while enlisting engaged supporters to contribute to the network. Unlike traditional crowdfunding, Core purchasers are invested in a project's long-term success, ensuring a foundation for lasting community and support.</span></p>`,
  },
  {
    question: 'Why Would Investors Buy a Core?',
    answer: `<ul>
      <li style="font-weight: 400;"><strong>Reward Structures:</strong><span style="font-weight: 400;"> Investors receive a share of network rewards based on their core uptime and participation.</span></li>
      <li style="font-weight: 400;"><strong>Long-Term Gains:</strong><span style="font-weight: 400;"> Cores often distribute project-specific tokens over time, reducing initial sell pressure.</span></li>
      <li style="font-weight: 400;"><strong>Additional Perks:</strong><span style="font-weight: 400;"> Investors may receive discounts, access to exclusive content, or other benefits to incentivize participation.</span></li>
      </ul>
    `,
  },
  {
    question: 'Token Distribution & Sustainability',
    answer: `<p><span style="font-weight: 400;">Many projects distribute their tokens primarily through a core network, ensuring continued investor support and long-term distribution. Compared to ICOs, IDOs, or private sales, cores provide a more sustainable and decentralized token distribution model.</span></p>
<br/>
<h3><strong>Key Benefits:</strong></h3>
<ul>
<li style="font-weight: 400;"><strong>Reduces Immediate Sell Pressure:</strong><span style="font-weight: 400;"> Gradual token releases stabilize pricing over time.</span></li>
<li style="font-weight: 400;"><strong>Ecosystem Rewards:</strong><span style="font-weight: 400;"> Core operators may receive a share of network income or fees.</span></li>
<li style="font-weight: 400;"><strong>Sustainable Distribution:</strong><span style="font-weight: 400;"> Scarcity is maintained as tokens are only distributed through Cores.</span></li>
</ul>`,
  },
  {
    question: 'Why Sell Cores Instead of a Direct Token Sale?',
    answer: `<p><span style="font-weight: 400;">Unlike token sales, which distribute a large supply at once and create immediate sell pressure, core sales distribute tokens gradually. Core owners operate for future yield, ensuring sustained engagement and long-term ecosystem stability.</span></p>
<br/>
<h3><strong>Funding Benefits:</strong></h3>
<ul>
<li style="font-weight: 400;"><strong>Higher Capital Potential:</strong><span style="font-weight: 400;"> Successful core sales can raise more funds than traditional token sales.</span></li>
<li style="font-weight: 400;"><strong>Tiered Pricing Model:</strong><span style="font-weight: 400;"> Cores increase in price over time, allowing capital raising to scale with demand.</span></li>
<li style="font-weight: 400;"><strong>Mitigates Valuation Impact:</strong><span style="font-weight: 400;"> Unlike token sales, core sales don't lead to massive early dilution.</span></li>
</ul>`,
  },
  {
    question: 'LFG Core Launchpad',
    answer: `<p><strong>Empowering Projects with Cores</strong></p>
<ul>
<li style="font-weight: 400;"><strong>Self-Funding:</strong><span style="font-weight: 400;"> Generate sustainable funds without relying on VCs or grants.</span></li>
<li style="font-weight: 400;"><strong>Ownership &amp; Control:</strong><span style="font-weight: 400;"> Projects maintain decentralized infrastructure ownership.</span></li>
<li style="font-weight: 400;"><strong>Community Engagement:</strong><span style="font-weight: 400;"> Cores incentivize participation and governance.</span></li>
</ul>
<br/>
<h3><strong>Core Use Cases</strong></h3>
<ul>
<li style="font-weight: 400;"><strong>Last Expedition:</strong><span style="font-weight: 400;"> Uses cores as distributed game servers.</span></li>
<li style="font-weight: 400;"><strong>Gala Music:</strong><span style="font-weight: 400;"> Utilizes Jukebox cores for global music streaming.</span></li>
<li style="font-weight: 400;"><strong>IPFS Support:</strong><span style="font-weight: 400;"> Founders Cores contribute to IPFS DHT servers, enhancing decentralized content storage.</span></li>
</ul>`,
  },
  {
    question: 'Technical Requirements',
    answer: `<p><span style="font-weight: 400;">To run a core, ensure your system meets these minimum specifications:</span></p>
<ul>
<li style="font-weight: 400;"><strong>CPU:</strong><span style="font-weight: 400;"> 3 or more cores</span></li>
<li style="font-weight: 400;"><strong>RAM:</strong><span style="font-weight: 400;"> 6 GB</span></li>
<li style="font-weight: 400;"><strong>Storage:</strong><span style="font-weight: 400;"> 60 GB</span></li>
<li style="font-weight: 400;"><strong>OS:</strong><span style="font-weight: 400;"> Windows, Mac, or Linux (64-bit)</span></li>
<li style="font-weight: 400;"><strong>Internet:</strong><span style="font-weight: 400;"> Stable connection</span></li>
<li style="font-weight: 400;"><em><span style="font-weight: 400;">Note: Requirements may change based on project needs.</span></em></li>
</ul>`,
  },
  {
    question: 'Purchase and Licensing',
    answer: `<h3><strong>Accepted Payment Methods for Purchase:</strong></h3>
<ul>
<li style="font-weight: 400;"><span style="font-weight: 400;">Ethereum</span></li>
<ul>
<li style="font-weight: 400;"><span style="font-weight: 400;">ETH, GALA, USDT, and USDC</span></li>
</ul>
</ul>
<br/>
<h3><strong>Core NFT Licensing</strong></h3>
<ul>
<li style="font-weight: 400;"><span style="font-weight: 400;">Initially sold as Ethereum NFTs for easy onboarding. Support for Base, Arbitrum, BSC, and other networks is coming soon, enabling projects to sell Core licenses on their chosen blockchain.</span></li>
</ul>
<br/>
<h3><strong>Transferability &amp; Refunds</strong></h3>
<ul>
<li style="font-weight: 400;"><span style="font-weight: 400;">Core NFTs are transferable, although some projects may have lock-up periods.</span></li>
<li style="font-weight: 400;"><span style="font-weight: 400;">Purchases are generally non-refundable, except in cases where minimum sale thresholds aren't met.</span></li>
</ul>`,
  },
  {
    question: 'Program Features and Benefits',
    answer: `<ol>
<li style="font-weight: 400;"><strong>Secure &amp; Efficient Operations</strong><span style="font-weight: 400;"> &ndash; Cores can validate transactions and create a tamper-proof ledger.</span></li>
<li style="font-weight: 400;"><strong>Robust Decentralized Infrastructure</strong><span style="font-weight: 400;"> &ndash; Cores can improve redundancy and network performance.</span></li>
<li style="font-weight: 400;"><strong>Leveraging Unused Computing Power</strong><span style="font-weight: 400;"> &ndash; Cores utilize excess resources for complex workloads.</span></li>
<li style="font-weight: 400;"><strong>Community Engagement &amp; Decision-Making</strong><span style="font-weight: 400;"> &ndash; Cores can incentivize participation and governance.</span></li>
<li style="font-weight: 400;"><strong>Ownership &amp; Control</strong><span style="font-weight: 400;"> &ndash; Projects retain infrastructure independence.</span></li>
</ol>`,
  },
  {
    question: 'Comparing Core Sales vs. Traditional Fundraising',
    answer: `<ul>
<li style="font-weight: 400;"><strong>Sustained Engagement:</strong><span style="font-weight: 400;"> Unlike ICOs and IDOs, core sales integrate funding with network growth.</span></li>
<li style="font-weight: 400;"><strong>Gradual Token Distribution:</strong><span style="font-weight: 400;"> Limits sell pressure, ensuring long-term stability.</span></li>
<li style="font-weight: 400;"><strong>Proven Market Success:</strong><span style="font-weight: 400;"> Projects like Gala Games, Aethir, and Sophon have raised millions through core sales.</span></li>
</ul>`,
  },
  {
    question: 'How to Get Started',
    answer: `<ul>
<li style="font-weight: 400;"><strong>Participate in a Core Sale:</strong><span style="font-weight: 400;"> Visit the Explore page on launch.lfg.inc.</span></li>
<li style="font-weight: 400;"><strong>Join the LFG Community:</strong><span style="font-weight: 400;"> Stay updated via Discord for latest developments.</span></li>
<li style="font-weight: 400;"><strong>Launch Your Own Core Sale:</strong><span style="font-weight: 400;"> Launch the app to start configuring your Core Sale.</span></li>
</ul>
<br/>
<p><span style="font-weight: 400;">At LFG, we help projects design, deploy, and optimize Core sales to achieve sustainable funding</span><strong>.</strong><span style="font-weight: 400;"> Let's build the future of decentralized innovation together!</span></p>`,
  },
];

export const HOW_IT_WORKS: HowItWorkItem[] = [
  {
    src: howItWorks.how_it_works_1,
    srcMobile: howItWorks.how_it_works_1_mobile,
    title: 'connect wallet',
  },
  {
    src: howItWorks.how_it_works_2,
    srcMobile: howItWorks.how_it_works_2_mobile,
    title: 'set up & customize',
  },
  {
    src: howItWorks.how_it_works_3,
    srcMobile: howItWorks.how_it_works_3_mobile,
    title: 'launch & scale',
  },
];
